import React from 'react';
import { Link } from 'react-router-dom';
import content from './content-home.json';
import { categoryBySlug } from './categories';
import { ArrowRight } from 'lucide-react';
import ProjectCard from './ProjectCard';
import { logEvent } from './analytics';
import { imgProps } from './img';

const { counts } = content;   // per-category totals across all projects (scripts/fetch-content.js)
// Exactly the projects marked "Show on home page" in /admin, in their /admin order (two rows of three).
const cards = content.projects.filter((p) => p.featured).slice(0, 6);

// The home page shows only the main platforms, in one row; the full filter list lives on /projects.
// Phones: every chip in a swipeable row that fades out on the right, so it reads as "more this way".
// Tablets show 5 and desktop 7, so the row never wraps there.
const HOME_CHIPS = ['shopify', 'wordpress', 'woocommerce', 'bigcommerce', 'gohighlevel', 'government', 'web-apps']
  .filter((slug) => counts[slug]);
const visibility = (i) => (i < 5 ? 'inline-flex' : 'inline-flex sm:hidden lg:inline-flex');

// Phone row: small screenshot, title, platform and one line of summary; the whole row opens the project.
const MiniRow = ({ p, darkMode }) => {
  const to = p.isCollection ? p.link : `/project/${p.slug}`;
  const platform = categoryBySlug((p.categories || [])[0]);
  return (
    <li>
      <Link to={to} onClick={() => logEvent('Project', 'Open', p.title)}
        className={`flex items-center gap-3 p-2 rounded-xl border transition-colors ${
          darkMode ? 'bg-black border-gray-800 hover:border-gray-600' : 'bg-gray-50 border-gray-200 hover:border-gray-400'}`}>
        <span className="flex-shrink-0 w-20 aspect-[16/10] rounded-lg overflow-hidden bg-gradient-to-br from-gray-800 to-black">
          {p.image && <img {...imgProps(p.image, '80px')} alt="" loading="lazy" decoding="async" width="160" height="100"
            className="w-full h-full object-cover object-top" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-sm font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{p.title}</span>
          <span className={`block text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {platform ? `${platform.label} · ` : ''}{p.summary || p.display_url}
          </span>
        </span>
        <ArrowRight size={14} className={`flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} aria-hidden="true" />
      </Link>
    </li>
  );
};

// Home page "Recent Projects": full width, three per row, with links into the main filter pages.
const FeaturedWork = ({ darkMode, sectionClass }) => (
  <section className={sectionClass} aria-labelledby="recent-projects">
    <div className="flex items-center justify-between mb-3 sm:mb-4">
      <div className="flex items-center gap-2">
        <svg className={`w-5 h-5 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        <h2 id="recent-projects" className={`text-lg leading-tight font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Recent Projects</h2>
      </div>
      <Link to="/projects" className={`-my-3 text-sm font-medium transition-colors duration-300 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
        View All →
      </Link>
    </div>
    <nav aria-label="Browse projects by platform" className="no-scrollbar flex flex-nowrap gap-2 mb-4 overflow-x-auto sm:overflow-hidden pr-16 sm:pr-0 [mask-image:linear-gradient(to_right,black_80%,transparent)] sm:[mask-image:none]">
      {HOME_CHIPS.map((slug, i) => (
        <Link key={slug} to={`/projects/${slug}`}
          className={`${visibility(i)} tap-compact flex-shrink-0 items-center gap-1.5 h-8 px-3 rounded-full text-xs font-medium border whitespace-nowrap ${
            darkMode ? 'border-gray-800 text-gray-300 hover:border-gray-600' : 'border-gray-300 text-gray-700 hover:border-gray-500'}`}>
          {categoryBySlug(slug).label} <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{counts[slug]}</span>
        </Link>
      ))}
    </nav>
    {/* Phones: compact rows (thumbnail + title). Tablets: 2 columns. Desktop: 3 columns. */}
    <ul className="sm:hidden space-y-2">
      {cards.map((p) => <MiniRow key={p.slug} p={p} darkMode={darkMode} />)}
    </ul>
    <ul className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((p) => (
        <li key={p.slug}>
          <ProjectCard project={p} darkMode={darkMode} compact />
        </li>
      ))}
    </ul>
  </section>
);

export default FeaturedWork;
